import React from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import isValid from 'date-fns/isValid';
import format from 'date-fns/format';
import parse from 'date-fns/parse';

/* eslint-disable-next-line import/prefer-default-export */
export const RenderDatePicker = ({ input, className, placeholder, popperPlacement, popperModifiers, isClearable, preventKeyboardDisplay, dateFormat, maxDate, disabled }) => {
  let initialDate = null;
  if (input.value) {
    initialDate = parse(input.value, dateFormat, new Date());
  }
  const [startDate, setStartDate] = React.useState(isValid(initialDate) ? initialDate : null);
  const datePicker = React.useRef(null);

  function isTouchDevice() {
    try {
      const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ');

      const mq = function (query) {
        return window.matchMedia(query).matches;
      };

      if (('ontouchstart' in window) || (typeof window.DocumentTouch !== 'undefined' && document instanceof window.DocumentTouch)) {
        return true;
      }

      return mq(['(', prefixes.join('touch-enabled),('), 'testtouch', ')'].join(''));
    } catch (e) {
      return false;
    }
  }

  function isIOS() {
    return !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);
  }

  function onHandleKeyDown(e) {
    if (e.keyCode === 8) {
      setStartDate(null);
      input.onChange(null);
    }
  }

  function handleFocus(e) {
    if (isTouchDevice() && preventKeyboardDisplay && datePicker != null && datePicker.current != null && datePicker.current.input != null) {
      // Switching the reaadonly property of the input prevents the keyboard from display on touch devices.
      if (!datePicker.current.input.readOnly && isIOS()) {
        datePicker.current.input.blur();
      }
      datePicker.current.input.readOnly = true;
    }
  }

  function handleBlur(e) {
    if (isTouchDevice() && !isIOS() && preventKeyboardDisplay && datePicker != null && datePicker.current != null && datePicker.current.input != null) {
      // Switching the reaadonly property of the input prevents the keyboard from display on touch devices.
      datePicker.current.input.readOnly = false;
    }
  }

  React.useEffect(() => {
    let newDate = null;
    if (input.value) {
      newDate = parse(input.value, dateFormat, new Date());
    }
    setStartDate(isValid(newDate) ? newDate : null);
  }, [input.value]);

  return (
    <DatePicker
      name={ input.name }
      ref={ datePicker }
      placeholder={ placeholder }
      fixedHeight
      showMonthDropdown
      placeholderText={ placeholder }
      maxDate={ maxDate }
      popperPlacement={ popperPlacement }
      popperModifiers={ popperModifiers }
      autoComplete="off"
      isClearable={ isClearable }
      showYearDropdown
      dropdownMode="select"
      className={ className === '' ? 'form-select' : className }
      dateFormat={ dateFormat }
      selected={ startDate }
      disabled={ disabled }
      onKeyDown={ e => onHandleKeyDown(e) }
      onChange={ (date) => { setStartDate(date); input.onChange(format(date, dateFormat)); } }
      onFocus={ e => handleFocus(e) }
      onBlur={ e => handleBlur(e) }
    />
  );
};

RenderDatePicker.propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    name: PropTypes.string,
    readOnly: PropTypes.bool
  }).isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  popperPlacement: PropTypes.string,
  popperModifiers: PropTypes.oneOfType([PropTypes.shape, PropTypes.arrayOf(PropTypes.shape())]),
  preventKeyboardDisplay: PropTypes.bool,

  isClearable: PropTypes.bool,
  dateFormat: PropTypes.string,
  maxDate: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
};

RenderDatePicker.defaultProps = {
  placeholder: '',
  popperPlacement: 'bottom-start',
  popperModifiers: [],
  className: '',
  isClearable: false,
  preventKeyboardDisplay: false,
  dateFormat: 'MM-dd-yyyy',
  maxDate: null,
  disabled: false
};
